import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SignalRService, SignalRMessage } from '../../services/signalr.service';
import { SignalRStatusComponent } from '../signalr-status/signalr-status.component';

interface NotificationDemo {
  id: string;
  type: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
}

interface OrderUpdateDemo {
  orderId: string;
  status: string;
  progress: number;
  location?: string;
  timestamp: Date;
}

interface SystemAlertDemo {
  id: string;
  level: 'info' | 'warning' | 'error' | 'success';
  message: string;
  timestamp: Date;
  acknowledged: boolean;
}

@Component({
  selector: 'app-signalr-demo',
  standalone: true,
  imports: [CommonModule, FormsModule, SignalRStatusComponent],
  templateUrl: './signalr-demo.component.html',
  styleUrl: './signalr-demo.component.css'
})
export class SignalRDemoComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Demo data
  notifications: NotificationDemo[] = [];
  orderUpdates: OrderUpdateDemo[] = [];
  systemAlerts: SystemAlertDemo[] = [];
  
  // Statistics
  stats = {
    totalMessages: 0,
    notifications: 0,
    orderUpdates: 0,
    systemAlerts: 0,
    unreadNotifications: 0,
    unacknowledgedAlerts: 0
  };

  // Demo controls
  autoGenerateMessages = false;
  messageInterval = 5000; // 5 seconds
  private messageTimer: any;

  // Message composition
  newMessage = {
    type: 'notification',
    title: 'Test Notification',
    content: 'This is a test message from SignalR demo',
    targetUser: '',
    targetGroup: ''
  };

  messageTypes = [
    { value: 'notification', label: 'Notification' },
    { value: 'orderUpdate', label: 'Order Update' },
    { value: 'systemAlert', label: 'System Alert' },
    { value: 'preAlertUpdate', label: 'Pre-Alert Update' },
    { value: 'documentUpdate', label: 'Document Update' }
  ];

  constructor(private signalRService: SignalRService) {}

  ngOnInit(): void {
    // Subscribe to different message types
    this.subscribeToMessages();
    
    // Start auto-generation if enabled
    if (this.autoGenerateMessages) {
      this.startAutoGeneration();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAutoGeneration();
  }

  private subscribeToMessages(): void {
    // General notifications
    this.signalRService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        this.handleNotification(message);
      });

    // Order updates
    this.signalRService.orderUpdates$
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        this.handleOrderUpdate(message);
      });

    // System alerts
    this.signalRService.systemAlerts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        this.handleSystemAlert(message);
      });

    // All messages for statistics
    this.signalRService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        this.updateStats(message);
      });
  }

  private handleNotification(message: SignalRMessage): void {
    const notification: NotificationDemo = {
      id: Date.now().toString(),
      type: message.data.type || 'info',
      title: message.data.title || 'Notification',
      message: message.data.message || JSON.stringify(message.data),
      timestamp: message.timestamp,
      read: false
    };

    this.notifications.unshift(notification);
    if (this.notifications.length > 50) {
      this.notifications = this.notifications.slice(0, 50);
    }
  }

  private handleOrderUpdate(message: SignalRMessage): void {
    const orderUpdate: OrderUpdateDemo = {
      orderId: message.data.orderId || `ORDER-${Date.now()}`,
      status: message.data.status || 'Updated',
      progress: message.data.progress || Math.floor(Math.random() * 100),
      location: message.data.location,
      timestamp: message.timestamp
    };

    // Update existing or add new
    const existingIndex = this.orderUpdates.findIndex(o => o.orderId === orderUpdate.orderId);
    if (existingIndex >= 0) {
      this.orderUpdates[existingIndex] = orderUpdate;
    } else {
      this.orderUpdates.unshift(orderUpdate);
    }

    if (this.orderUpdates.length > 20) {
      this.orderUpdates = this.orderUpdates.slice(0, 20);
    }
  }

  private handleSystemAlert(message: SignalRMessage): void {
    const alert: SystemAlertDemo = {
      id: Date.now().toString(),
      level: message.data.level || 'info',
      message: message.data.message || JSON.stringify(message.data),
      timestamp: message.timestamp,
      acknowledged: false
    };

    this.systemAlerts.unshift(alert);
    if (this.systemAlerts.length > 30) {
      this.systemAlerts = this.systemAlerts.slice(0, 30);
    }
  }

  private updateStats(message: SignalRMessage): void {
    this.stats.totalMessages++;
    
    switch (message.type) {
      case 'notification':
      case 'preAlertUpdate':
      case 'documentUpdate':
        this.stats.notifications++;
        break;
      case 'orderUpdate':
        this.stats.orderUpdates++;
        break;
      case 'systemAlert':
        this.stats.systemAlerts++;
        break;
    }

    this.stats.unreadNotifications = this.notifications.filter(n => !n.read).length;
    this.stats.unacknowledgedAlerts = this.systemAlerts.filter(a => !a.acknowledged).length;
  }

  // Message sending methods
  async sendCustomMessage(): Promise<void> {
    try {
      const messageData = {
        title: this.newMessage.title,
        message: this.newMessage.content,
        timestamp: new Date().toISOString()
      };

      switch (this.newMessage.type) {
        case 'notification':
          await this.signalRService.sendNotification('custom', messageData, this.newMessage.targetUser || undefined);
          break;
        case 'orderUpdate':
          await this.signalRService.sendOrderUpdate({
            ...messageData,
            orderId: `CUSTOM-${Date.now()}`,
            status: 'Custom Update'
          }, this.newMessage.targetGroup || undefined);
          break;
        case 'systemAlert':
          await this.signalRService.sendSystemAlert({
            ...messageData,
            level: 'info'
          });
          break;
        default:
          await this.signalRService.sendMessage('ReceiveMessage', this.newMessage.type, messageData);
      }

      // Reset form
      this.newMessage.content = '';
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  // Demo message generators
  generateRandomNotification(): void {
    const notifications = [
      { type: 'info', title: 'Information', message: 'New shipment has arrived at the warehouse' },
      { type: 'success', title: 'Success', message: 'Order has been successfully processed' },
      { type: 'warning', title: 'Warning', message: 'Shipment delay detected for Order #12345' },
      { type: 'update', title: 'Update', message: 'Pre-alert status has been updated' }
    ];

    const notification = notifications[Math.floor(Math.random() * notifications.length)];
    this.signalRService.sendNotification(notification.type, {
      title: notification.title,
      message: notification.message,
      timestamp: new Date().toISOString()
    }).catch(console.error);
  }

  generateRandomOrderUpdate(): void {
    const statuses = ['Processing', 'Shipped', 'In Transit', 'Delivered', 'Customs Cleared'];
    const locations = ['Singapore', 'Manila', 'Cebu', 'Davao', 'Warehouse A', 'Distribution Center'];
    
    const orderId = `ORD-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const location = locations[Math.floor(Math.random() * locations.length)];
    const progress = Math.floor(Math.random() * 100);

    this.signalRService.sendOrderUpdate({
      orderId,
      status,
      location,
      progress,
      timestamp: new Date().toISOString()
    }).catch(console.error);
  }

  generateRandomSystemAlert(): void {
    const levels = ['info', 'warning', 'error', 'success'];
    const messages = [
      'System maintenance scheduled for tonight',
      'Database backup completed successfully',
      'High CPU usage detected on server',
      'New integration endpoint activated',
      'Unusual traffic pattern detected'
    ];

    const level = levels[Math.floor(Math.random() * levels.length)];
    const message = messages[Math.floor(Math.random() * messages.length)];

    this.signalRService.sendSystemAlert({
      level,
      message,
      timestamp: new Date().toISOString()
    }).catch(console.error);
  }

  // Auto-generation controls
  toggleAutoGeneration(): void {
    this.autoGenerateMessages = !this.autoGenerateMessages;
    
    if (this.autoGenerateMessages) {
      this.startAutoGeneration();
    } else {
      this.stopAutoGeneration();
    }
  }

  startAutoGeneration(): void {
    this.stopAutoGeneration(); // Clear any existing timer
    
    this.messageTimer = setInterval(() => {
      const messageType = Math.floor(Math.random() * 3);
      
      switch (messageType) {
        case 0:
          this.generateRandomNotification();
          break;
        case 1:
          this.generateRandomOrderUpdate();
          break;
        case 2:
          this.generateRandomSystemAlert();
          break;
      }
    }, this.messageInterval);
  }

  private stopAutoGeneration(): void {
    if (this.messageTimer) {
      clearInterval(this.messageTimer);
      this.messageTimer = null;
    }
  }

  // UI interaction methods
  markNotificationAsRead(notification: NotificationDemo): void {
    notification.read = true;
    this.updateStatsFromUI();
  }

  acknowledgeAlert(alert: SystemAlertDemo): void {
    alert.acknowledged = true;
    this.updateStatsFromUI();
  }

  private updateStatsFromUI(): void {
    this.stats.unreadNotifications = this.notifications.filter(n => !n.read).length;
    this.stats.unacknowledgedAlerts = this.systemAlerts.filter(a => !a.acknowledged).length;
  }

  clearNotifications(): void {
    this.notifications = [];
    this.updateStatsFromUI();
  }

  clearOrderUpdates(): void {
    this.orderUpdates = [];
  }

  clearSystemAlerts(): void {
    this.systemAlerts = [];
    this.updateStatsFromUI();
  }

  clearAllData(): void {
    this.notifications = [];
    this.orderUpdates = [];
    this.systemAlerts = [];
    this.stats = {
      totalMessages: 0,
      notifications: 0,
      orderUpdates: 0,
      systemAlerts: 0,
      unreadNotifications: 0,
      unacknowledgedAlerts: 0
    };
  }

  // Utility methods
  getAlertClass(level: string): string {
    switch (level) {
      case 'success': return 'alert-success';
      case 'warning': return 'alert-warning';
      case 'error': return 'alert-danger';
      case 'info':
      default: return 'alert-info';
    }
  }

  getProgressBarClass(progress: number): string {
    if (progress < 30) return 'bg-danger';
    if (progress < 70) return 'bg-warning';
    return 'bg-success';
  }

  formatTimestamp(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(date);
  }

  formatRelativeTime(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  }
}